import { Badge } from '@quickmd-llc/design-system'
import { getGrantVisitBadgeLabel } from '@quickmd-llc/frontend-utils'

type Props = {
  className?: string
  /**
   * The visit's `Coverage` block. `unknown` for the same reason the plan chip
   * takes it that way: some call sites hold the typed contract, others read the
   * visit out of untyped legacy Redux, and `getGrantVisitBadgeLabel` narrows it.
   */
  coverage: unknown
}

/**
 * The chip a grant-funded visit wears wherever a clinician sees that visit — the
 * grant program's own name (`MS Grant`). Renders nothing when the visit was not
 * grant-funded, so callers drop it in unconditionally instead of repeating the
 * guard.
 *
 * Unlike the plan chip there is no feature flag: a grant either paid for the
 * visit or it did not, and that fact is not something to roll out.
 *
 * The `tailwind-scoped` wrapper is not decoration, and matches the plan chip's
 * wrapper for the same reason: `src/index.css` nests every `@tailwind` directive
 * inside `.tailwind-scoped`, so a design-system component dropped on a legacy
 * SCSS page (the patient chart, the past-visit notes) would otherwise get none of
 * its utilities and render as unstyled wrapping text. Scoping here rather than at
 * each call site means it cannot be got wrong the next time the chip moves.
 */
export function GrantVisitBadge({ className, coverage }: Props) {
  const label = getGrantVisitBadgeLabel(coverage)

  if (!label) {
    return null
  }

  return (
    <span className='tailwind-scoped tailwind-scoped-contents'>
      <Badge className={className} size='s' variant='default'>
        {label}
      </Badge>
    </span>
  )
}
